// Paginación canónica del sistema de diseño — para datos MASIVOS.
//
// Regla de diseño: cualquier lista que pueda crecer a decenas/cientos de
// elementos se pagina POR DEFAULT con `usePaginated` + <Pagination>. No renderizar
// listas largas completas ni reinventar controles por página. Theme-aware por
// tokens semánticos.

import { useMemo, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Tamaño de página default del sistema. Un único lugar para ajustarlo.
export const DEFAULT_PAGE_SIZE = 12;

// Hook: recibe el arreglo completo, devuelve la página actual + controles.
export function usePaginated<T>(items: T[], pageSize: number = DEFAULT_PAGE_SIZE) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageItems = useMemo(
    () => items.slice(safePage * pageSize, safePage * pageSize + pageSize),
    [items, safePage, pageSize],
  );
  return {
    pageItems,
    page: safePage,
    pageCount,
    total: items.length,
    pageSize,
    setPage,
    from: items.length === 0 ? 0 : safePage * pageSize + 1,
    to: Math.min((safePage + 1) * pageSize, items.length),
  };
}

// Controles. Se ocultan solos si hay una sola página (nada que paginar).
export function Pagination({ page, pageCount, total, from, to, onPage, label = 'elementos' }: {
  page: number; pageCount: number; total: number; from: number; to: number;
  onPage: (p: number) => void; label?: string;
}): ReactNode {
  if (pageCount <= 1) return null;
  return (
    <div className="flex items-center justify-between gap-3 pt-2 text-xs text-muted-foreground">
      <span>{from}–{to} de {total} {label}</span>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(page - 1)} disabled={page === 0}
          className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-foreground disabled:opacity-40 hover:bg-muted">
          <ChevronLeft size={14} /> Anterior
        </button>
        <span className="px-2 tabular-nums">{page + 1} / {pageCount}</span>
        <button onClick={() => onPage(page + 1)} disabled={page >= pageCount - 1}
          className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-foreground disabled:opacity-40 hover:bg-muted">
          Siguiente <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
